import { Fragment, useMemo } from 'react'

import { Icon } from '@wartek-id/icon-v2'
import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'

import { PANDUAN_PENGELOLAAN_URL } from 'app/KinerjaShared/utils/panduan'

type PengelolaanKinerjaTimelineProps = {
  title?: string
  timeline: Array<TimelineItem>
}

type TimelineItem = {
  title: string
  steps: Array<string>
}

const DEFAULT_TITLE = 'Rekomendasi Linimasa Pengelolaan Kinerja'

export function PengelolaanKinerjaTimeline({
  timeline,
  title = DEFAULT_TITLE,
}: PengelolaanKinerjaTimelineProps) {
  // Memoize header items with titles, steps length, and grid positions
  const headerItems = useMemo(() => {
    return timeline.map((item, index) => {
      /**
       * Each item's grid span: 2 columns per step, minus 1 for overlap.
       */
      const span = item.steps.length * 2 - 1

      /**
       * Grid column start:
       * - Start at 1 (CSS grid is 1-based)
       * - Add 'index' for spacing columns between items
       * - Add all previous items' spans for correct positioning
       */
      const colStart =
        1 +
        index +
        timeline
          .slice(0, index)
          .reduce((acc, t) => acc + (t.steps.length * 2 - 1), 0)

      /**
       * Grid column end: exclusive, so add span to start.
       */
      const colEnd = colStart + span

      return {
        title: item.title,
        stepsLength: item.steps.length,
        colStart,
        colEnd,
      }
    })
  }, [timeline])

  // Memoize flattened steps array
  const flattenedSteps = useMemo(() => {
    return timeline.map((item) => item.steps).flat()
  }, [timeline])

  // Calculate grid template columns
  const gridTemplateColumns = useMemo(() => {
    const templateColumns = flattenedSteps.map((_step, index) => {
      const isLastStep = index === flattenedSteps.length - 1
      if (isLastStep) return '1fr'
      return '1fr 24px'
    })
    return templateColumns.join(' ')
  }, [flattenedSteps])

  return (
    <section className="shadow-card rounded-lg bg-white">
      <div className="flex items-center gap-4 p-6 w-full border-b border-subdued">
        <Text variant="heading-md" className="w-full" as="h2">
          {title}
        </Text>
        <Button
          variant="ghost"
          className="space-x-1 whitespace-nowrap"
          as="a"
          type={undefined}
          href={PANDUAN_PENGELOLAAN_URL}
          target="blank"
        >
          Ke Pusat Informasi
          <Icon color="informational">chevron_right</Icon>
        </Button>
      </div>
      <div
        className="grid gap-6 p-6"
        style={{
          gridTemplateColumns,
        }}
      >
        {/* Header */}
        {headerItems.map((headerItem, index) => {
          return (
            <div
              key={index}
              style={{
                gridColumnStart: headerItem.colStart,
                gridColumnEnd: headerItem.colEnd,
                gridRowStart: 1,
              }}
              className="bg-blue-10 p-0.5 rounded-md text-center row-span-1"
            >
              <Text variant="action" className="!text-blue-60">
                {headerItem.title}
              </Text>
            </div>
          )
        })}

        {/* Steps Content (second row) */}
        {flattenedSteps.map((step, index) => {
          return (
            <Fragment key={index}>
              <div
                style={{
                  gridRowStart: 2,
                }}
                className="flex items-center"
              >
                <Text variant="body-sm" color="default">
                  {step}
                </Text>
              </div>
              {index < flattenedSteps.length - 1 && (
                <div
                  style={{ gridRowStart: 2 }}
                  className="text-center flex items-center justify-center"
                >
                  <Icon color="informational">arrow_circle_right</Icon>
                </div>
              )}
            </Fragment>
          )
        })}
      </div>
    </section>
  )
}
